import { Controller } from "@hotwired/stimulus"
import { phylotree } from "phylotree"

// Connects to data-controller="phylotree-viewer"
export default class extends Controller {
  static targets = ["container"]

  static values = {
    newick: String
  }

  connect() {
    console.log("PhylotreeViewer controller connected")
    console.log("Newick data length:", this.newickValue?.length)
    console.log("Has container target:", this.hasContainerTarget)

    // Use setTimeout to allow Stimulus to finish registering targets
    setTimeout(() => {
      console.log("setTimeout - Has container target:", this.hasContainerTarget)
      if (this.hasContainerTarget) {
        this.initialize()
      } else {
        console.error("Target still not available - checking DOM directly")
        // Last resort: try to find the element by selector
        const el = this.element.querySelector('[data-phylotree-viewer-target="container"]')
        console.log("Direct querySelector result:", el)
      }
    }, 0)
  }

  containerTargetConnected() {
    console.log("Container target connected!")
    // If tree is already created but not rendered, render it now
    if (this.tree && !this.treeRendered) {
      this.renderTree()
    }
    // Otherwise initialize if not already done
    else if (!this.tree) {
      this.initialize()
    }
  }

  initialize() {
    if (!this.newickValue || this.newickValue.trim() === '') {
      console.error("No Newick string provided")
      if (this.hasContainerTarget) {
        this.containerTarget.innerHTML = `
          <div class="alert alert-danger" role="alert">
            <strong>Error:</strong> No tree data available
          </div>
        `
      }
      return
    }

    try {
      this.tree = new phylotree(this.newickValue, { selectable: true })
      console.log("Tree created successfully")
      this.renderTree()
    } catch (error) {
      console.error("Error creating phylotree:", error)
      if (this.hasContainerTarget) {
        this.containerTarget.innerHTML = `
          <div class="alert alert-danger" role="alert">
            <strong>Error:</strong> ${error.message}
          </div>
        `
      }
    }
  }

  renderTree() {
    if (!this.hasContainerTarget) {
      console.error("Cannot render: container target not available")
      return
    }

    console.log("Rendering tree...")

    this.tree.render({
      container: `#${this.containerTarget.id}`,
      "draw-size-bubbles": false,
      "left-right-spacing": "fixed-step"
    })

    console.log("Tree rendered, appending display")
    this.containerTarget.innerHTML = ''
    this.containerTarget.appendChild(this.tree.display.show())
    this.treeRendered = true
    console.log("Tree displayed successfully")
  }

  // Toolbar actions
  expandVertical() {
    if (!this.tree) return
    const func = this.tree.display.spacing_x.bind(this.tree.display)
    func(func() + 1).update()
  }

  compressVertical() {
    if (!this.tree) return
    const func = this.tree.display.spacing_x.bind(this.tree.display)
    func(func() - 1).update()
  }

  expandHorizontal() {
    if (!this.tree) return
    const func = this.tree.display.spacing_y.bind(this.tree.display)
    func(func() + 1).update()
  }

  compressHorizontal() {
    if (!this.tree) return
    const func = this.tree.display.spacing_y.bind(this.tree.display)
    func(func() - 1).update()
  }

  sortAscending() {
    if (!this.tree) return
    this.tree.resortChildren((a, b) => {
      return (b.height - a.height || b.value - a.value)
    })
    this.tree.display.update()
  }

  sortDescending() {
    if (!this.tree) return
    this.tree.resortChildren((a, b) => {
      return (b.height - a.height || b.value - a.value) * -1
    })
    this.tree.display.update()
  }

  sortOriginal() {
    if (!this.tree) return
    this.tree.resortChildren((a, b) => {
      return a.original_child_order - b.original_child_order
    })
    this.tree.display.update()
  }

  toggleLinear() {
    if (!this.tree) return
    this.tree.display.radial(false).update()
  }

  toggleRadial() {
    if (!this.tree) return
    this.tree.display.radial(true).update()
  }

  alignLeft() {
    if (!this.tree) return
    this.tree.display.alignTips(false).update()
  }

  alignRight() {
    if (!this.tree) return
    this.tree.display.alignTips(true).update()
  }

  saveImage() {
    if (!this.tree || !this.hasContainerTarget) return

    const svg = this.containerTarget.querySelector("svg")
    if (!svg) return

    svg.setAttribute("version", "1.1")
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg")

    const source = new XMLSerializer().serializeToString(svg)
    const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" })
    const url = URL.createObjectURL(blob)

    const link = document.createElement("a")
    link.href = url
    link.download = "phylogenetic-tree.svg"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}
